import { Logger } from 'src/utils/Logger';

// Mirrors TenantCurrency in the backend's admin domain; kept in sync by hand, as
// AuthMethod is. Adding a currency means changing this map and that enum together.
export const TENANT_CURRENCY_LABELS = {
  GBP: 'Pound sterling (£)',
  EUR: 'Euro (€)',
} as const;

export type TenantCurrency = keyof typeof TENANT_CURRENCY_LABELS;

export const DEFAULT_CURRENCY: TenantCurrency = 'GBP';

// en-GB rather than a per-currency locale: it renders euros identically to en-IE
// (€1,234.56) while keeping dates and grouping consistent across tenants.
const LOCALE = 'en-GB';

export const buildCurrencyFormatter = (currency: TenantCurrency) =>
  new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
  });

/** The symbol alone, e.g. "£" or "€" — for input adornments and hand-built strings. */
export function currencySymbol(currency: TenantCurrency): string {
  const parts = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumSignificantDigits: 1,
  }).formatToParts(0);
  return parts.find(part => part.type === 'currency')?.value ?? currency;
}

/**
 * Money with its minor units always shown: "£3,500.00".
 * Use `formatMoney` instead where whole amounts should read as "£3,500".
 */
export function formatCurrency(
  value: unknown,
  currency: string,
  options?: { fractionDigits?: number },
): string | undefined {
  const castedNumber = Number(value);
  if (isNaN(castedNumber)) {
    Logger.warn('Value is not a number for MONEY', { value });
    return;
  }

  const formatterOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
  };

  if (options?.fractionDigits !== undefined) {
    formatterOptions.minimumFractionDigits = options.fractionDigits;
    formatterOptions.maximumFractionDigits = options.fractionDigits;
  }

  return new Intl.NumberFormat(LOCALE, formatterOptions).format(castedNumber);
}

/**
 * Money with minor units only when they are non-zero: "£3,500" but "£3,500.50".
 * Negatives render as "£-3,500"; use `formatSignedMoney` where a value can dip
 * below zero and should read as "-£3,500".
 */
export function formatMoney(
  amount: string | number | undefined | null,
  currency: TenantCurrency,
): string {
  const symbol = currencySymbol(currency);
  if (amount == null) {
    return `${symbol}0`;
  }
  const numeric = typeof amount === 'string' ? parseFloat(amount) : amount;

  const isWholeNumber = numeric % 1 === 0;
  return `${symbol}${numeric.toLocaleString(LOCALE, {
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * As `formatMoney`, but negatives read as "-£5,000" rather than "£-5,000", matching
 * the chart axis formatters. For money that can dip negative: net worth, legacy.
 */
export function formatSignedMoney(
  amount: string | number | undefined | null,
  currency: TenantCurrency,
): string {
  const numeric = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  return numeric < 0 ? `-${formatMoney(-numeric, currency)}` : formatMoney(numeric, currency);
}

/**
 * Short money for headline figures: "£1.24m", "£412k", "-£38k". Negatives read as
 * "-£38k", matching `formatSignedMoney`.
 */
export function formatCompactSignedMoney(amount: number, currency: TenantCurrency): string {
  const symbol = currencySymbol(currency);
  const sign = amount < 0 ? '-' : '';
  const value = Math.abs(amount);
  const trimmed = (scaled: number) => scaled.toFixed(2).replace(/\.?0+$/, '');
  // Thresholds sit just below each unit so rounding never reads as "£1000k".
  if (value >= 999_995_000) return `${sign}${symbol}${trimmed(value / 1_000_000_000)}bn`;
  if (value >= 999_500) return `${sign}${symbol}${trimmed(value / 1_000_000)}m`;
  if (value >= 999.5) return `${sign}${symbol}${Math.round(value / 1_000)}k`;
  return `${sign}${symbol}${Math.round(value)}`;
}

export function formatAbbreviatedCurrency(value: number, currency: TenantCurrency): string {
  const symbol = currencySymbol(currency);
  if (value >= 1_000_000_000) {
    return `${symbol}${(value / 1_000_000_000).toFixed(1)}bn`;
  }
  if (value >= 1_000_000) {
    return `${symbol}${(value / 1_000_000).toFixed(1)}m`;
  }
  if (value >= 1_000) {
    return `${symbol}${(value / 1_000).toFixed(1)}k`;
  }
  return `${symbol}${value.toFixed(0)}`;
}
